/**
 * UBM Accounts Page - Manage utility accounts
 */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api";
import { toast } from "sonner";
import {
  Hash,
  Plus,
  Search,
  Edit,
  Trash2,
  Building2,
  MapPin,
  Calendar,
  Upload,
  Download,
  Loader2,
  ChevronLeft,
  Play,
  Clock,
  CheckCircle,
  XCircle,
  Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SCHEDULE_OPTIONS = [
  { value: "monthly", label: "Monthly", description: "Collect latest bill each month" },
  { value: "past_3_months", label: "Past 3 Months", description: "Collect last 3 months of bills" },
  { value: "past_12_months", label: "Past 12 Months", description: "Collect last 12 months of bills" },
  { value: "all_months", label: "All Available", description: "Collect all available bills" },
  { value: "manual", label: "Manual Only", description: "Only trigger manually" }
];

export default function UBMAccounts() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [accounts, setAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("all");
  
  // Form state
  const [showDialog, setShowDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    account_number: "",
    vendor_id: "",
    service_address: "",
    city: "",
    state: "",
    postcode: "",
    schedule_frequency: "monthly",
    is_active: true
  });
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, account: null });
  
  // Trigger collection
  const [triggering, setTriggering] = useState(false);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  
  useEffect(() => {
    loadData();
  }, [orgId, selectedVendor]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load vendors
      const vendorsRes = await api.get(`/ubm/${orgId}/vendors`);
      setVendors(vendorsRes.data.vendors || []);
      
      // Load accounts
      let url = `/ubm/${orgId}/accounts`;
      if (selectedVendor && selectedVendor !== "all") {
        url += `?vendor_id=${selectedVendor}`;
      }
      
      const accountsRes = await api.get(url);
      setAccounts(accountsRes.data.accounts || []);
    } catch (error) {
      console.error("Failed to load accounts:", error);
      if (error.response?.status === 403) {
        toast.error("UBM module not enabled for this organization");
        navigate(`/org/${orgId}`);
      } else {
        toast.error("Failed to load accounts");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const openCreateDialog = () => {
    setEditingAccount(null);
    setForm({
      account_number: "",
      vendor_id: vendors[0]?.id || "",
      service_address: "",
      city: "",
      state: "",
      postcode: "",
      schedule_frequency: "monthly",
      is_active: true
    });
    setShowDialog(true);
  };
  
  const openEditDialog = (account) => {
    setEditingAccount(account);
    setForm({
      account_number: account.account_number || "",
      vendor_id: account.vendor_id || "",
      service_address: account.service_address || "",
      city: account.city || "",
      state: account.state || "",
      postcode: account.postcode || "",
      schedule_frequency: account.schedule_frequency || "monthly",
      is_active: account.is_active !== false
    });
    setShowDialog(true);
  };
  
  const handleSave = async () => {
    if (!form.account_number || !form.vendor_id) {
      toast.error("Account number and vendor are required");
      return;
    }
    
    try {
      setSaving(true);
      
      if (editingAccount) {
        await api.put(`/ubm/${orgId}/accounts/${editingAccount.id}`, form);
        toast.success("Account updated");
      } else {
        await api.post(`/ubm/${orgId}/accounts`, form);
        toast.success("Account created");
      }
      
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save account");
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!deleteDialog.account) return;
    
    try {
      await api.delete(`/ubm/${orgId}/accounts/${deleteDialog.account.id}`);
      toast.success("Account deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete account");
    } finally {
      setDeleteDialog({ open: false, account: null });
    }
  };
  
  const triggerCollection = async (accountIds = null) => {
    try {
      setTriggering(true);
      
      const payload = {};
      if (accountIds) {
        payload.account_ids = accountIds;
      } else if (selectedVendor && selectedVendor !== "all") {
        payload.vendor_id = selectedVendor;
      }
      
      const res = await api.post(`/ubm/${orgId}/trigger-collection`, payload);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger collection");
    } finally {
      setTriggering(false);
    }
  };
  
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await api.post(`/ubm/${orgId}/accounts/bulk-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(err => toast.warning(err));
      }
      
      loadData();
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  
  const downloadTemplate = () => {
    const headers = "account_number,vendor_id,service_address,city,state,postcode,schedule_frequency,is_active";
    const example = "123456789,vendor-id-here,123 Main St,Chicago,IL,60601,monthly,true";
    const csv = `${headers}\n${example}`;
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "accounts_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const filteredAccounts = accounts.filter(a => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      a.account_number?.toLowerCase().includes(query) ||
      a.vendor_name?.toLowerCase().includes(query) ||
      a.service_address?.toLowerCase().includes(query) ||
      a.city?.toLowerCase().includes(query)
    );
  });
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString();
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6" data-testid="ubm-accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/org/${orgId}`)}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Hash className="w-6 h-6 text-purple-400" />
              Accounts
            </h1>
            <p className="text-zinc-400 text-sm">Manage utility accounts and collection schedules</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => triggerCollection()}
            disabled={triggering || accounts.length === 0}
            className="border-green-600 text-green-400 hover:bg-green-600/10"
          >
            {triggering ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Trigger Collection
          </Button>
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Bulk Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleBulkUpload}
          />
          <Button onClick={openCreateDialog} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Account
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800 mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                placeholder="Search by account, vendor, address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
              <SelectTrigger className="w-[200px] bg-zinc-800 border-zinc-700">
                <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="All Vendors" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Accounts Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {accounts.length} Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Hash className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No accounts configured</p>
              <p className="text-sm">Add accounts to track and collect bills</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Account #</TableHead>
                  <TableHead className="text-zinc-400">Vendor</TableHead>
                  <TableHead className="text-zinc-400">Service Address</TableHead>
                  <TableHead className="text-zinc-400">Schedule</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400">Last Collection</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id} className="border-zinc-800">
                    <TableCell className="font-mono font-medium">
                      {account.account_number}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        <Building2 className="w-3 h-3 mr-1" />
                        {account.vendor_name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-1">
                        <MapPin className="w-3 h-3 mt-1 text-zinc-500 flex-shrink-0" />
                        <div className="text-sm">
                          <div>{account.service_address || "-"}</div>
                          {account.city && (
                            <div className="text-zinc-500 text-xs">
                              {account.city}, {account.state} {account.postcode}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        {SCHEDULE_OPTIONS.find(s => s.value === account.schedule_frequency)?.label || account.schedule_frequency}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {account.is_active ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-400">
                      {formatDate(account.last_collection_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => triggerCollection([account.id])}
                          disabled={triggering}
                          title="Trigger collection for this account"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(account)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => setDeleteDialog({ open: true, account })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? "Edit Account" : "Add Account"}
            </DialogTitle>
            <DialogDescription>
              Configure account details and collection schedule
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Account Number *</Label>
                <Input
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  placeholder="e.g., 123456789"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Vendor *</Label>
                <Select 
                  value={form.vendor_id} 
                  onValueChange={(v) => setForm({ ...form, vendor_id: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2">
                <Label>Service Address</Label>
                <Input
                  value={form.service_address}
                  onChange={(e) => setForm({ ...form, service_address: e.target.value })}
                  placeholder="Street address"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              
              <div>
                <Label>City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="City"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>State</Label>
                  <Input
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    placeholder="IL"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label>Postcode</Label>
                  <Input
                    value={form.postcode}
                    onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                    placeholder="60601"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
              </div>
              
              <div className="col-span-2">
                <Label>Collection Schedule</Label>
                <Select 
                  value={form.schedule_frequency} 
                  onValueChange={(v) => setForm({ ...form, schedule_frequency: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {SCHEDULE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div>
                          <div>{opt.label}</div>
                          <div className="text-xs text-zinc-500">{opt.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 flex items-center justify-between">
                <div>
                  <Label>Active</Label>
                  <p className="text-xs text-zinc-500">Enable automatic bill collection</p>
                </div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingAccount ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, account: null })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete account "{deleteDialog.account?.account_number}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
